import { useExplorerObjects } from '@/features/explorer/hooks/use-explorer-objects';
import { useEffect, useMemo, useState } from 'react';
import { Table } from '@/components/ui/table';
import {
  ColumnDef,
  PaginationState,
  RowSelectionState,
} from '@tanstack/react-table';
import { getRootDataClass } from '@/features/explorer/helpers';
import { useUserConfig } from '@/hooks/user/use-user-config';
import { useEmitter } from '@/hooks/use-emitter';
import { useDocumentTitle } from '@/hooks/use-document-title';

interface ExplorerTableProps {
  container: string;
  search: string;
  id: string;
  defaultEsQueries?: {
    container: string;
    query: string;
  }[];
}

export function ExplorerTable({
  container,
  id,
  search,
  defaultEsQueries,
}: ExplorerTableProps) {
  const idPrefix = id.split('/').slice(0, -1).join('/');
  const emit = useEmitter({
    id: id,
    listenersPrefix: idPrefix,
  });
  const { data: userConfig } = useUserConfig();
  const setTitle = useDocumentTitle();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const { data, isLoading } = useExplorerObjects({
    container,
    search,
    size: pagination.pageSize,
    from: pagination.pageIndex * pagination.pageSize,
    esQuery: defaultEsQueries?.find((query) => query.container === container)
      ?.query,
  });

  const tableData = useMemo(() => {
    return (data?.collectionData || []).map(({ data, ...rest }) => {
      return {
        ...data,
        nodeData: rest,
      };
    });
  }, [data]);

  const columns = useMemo<ColumnDef<any>[]>(() => {
    const columns = [];

    for (const [key, value] of Object.entries(
      data?.collectionSchema?.properties || {}
    )) {
      const { representations } = value;

      if (!representations?.exploreTable?.display) {
        continue;
      }

      columns.push({
        accessorKey: key,
        header: value.title,
        keyword: value.querySearchKeyword ?? false,
        size: value.tableWidth,
      });
    }

    return columns;
  }, [data?.collectionSchema]);

  const handlePaginationChange = (newPagination: PaginationState) => {
    setPagination(newPagination);
  };

  useEffect(() => {
    const objects = tableData.filter((_, index) => rowSelection[index]);

    if (objects.length === 0) {
      return;
    }

    const messages = [
      {
        type: 'ObjectCollection',
        data: objects.map((object) => ({
          uuid: object.nodeData.uuid,
          container: object.nodeData.root
            ? object.nodeData.uuid
            : object.nodeData.container,
          publicUrl: object.nodeData?.publicUrl,
          root: object.nodeData.root,
          dataClassUuid: object.nodeData.root
            ? getRootDataClass(
                object.nodeData.uuid,
                userConfig?.organisationConfig.dataClasses || []
              )
            : object.nodeData.dataClassUuid,
        })),
      },
      {
        type: 'Preview',
        data: objects.map((object) => ({
          uuid: object.nodeData.uuid,
          container: object.nodeData.container,
          previewUuid: object.nodeData.previewUuid,
        })),
      },
    ];

    emit(messages);

    setTitle(objects[0].nodeData.label || objects[0].nodeData.uuid);
  }, [rowSelection]);

  return (
    <div className="pt-20">
      <Table
        columns={columns}
        data={tableData}
        isLoading={isLoading}
        manualPagination
        pageCount={
          data?.total ? Math.ceil(data.total / pagination.pageSize) : 0
        }
        onPaginationChange={handlePaginationChange}
        showSearch={false}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
      />
    </div>
  );
}
